// src/components/EmailList.jsx
import React, { useMemo, memo } from 'react';
import { ListGroup, Badge } from 'react-bootstrap';
import './EmailList.css';

// Memoized email item component to prevent unnecessary re-renders
const EmailItem = memo(({ email, isSelected, onSelect, formatDate }) => {
  return (
    <ListGroup.Item
      action
      active={isSelected}
      onClick={() => onSelect(email.id)}
      className={`email-item ${isSelected ? 'active' : ''}`}
    >
      <div className="d-flex justify-content-between align-items-start">
        <div className="email-sender fw-bold text-truncate">{email.from || 'No Sender'}</div>
        <small className="email-date">{formatDate(email.date)}</small>
      </div>
      <div className="email-subject text-truncate">{email.subject || '(No Subject)'}</div>
      <div className="d-flex align-items-center mt-1">
        <small className="email-recipient text-truncate text-muted">
          To: {email.to || 'Unknown'}
        </small>
        {email.hasAttachments && (
          <Badge bg="light" text="dark" className="ms-2">
            <i className="bi bi-paperclip"></i>
          </Badge>
        )}
        {email.isUnread && (
          <Badge bg="primary" className="ms-2">
            New
          </Badge>
        )}
      </div>
    </ListGroup.Item>
  );
});

function EmailList({ emails, onSelectEmail, selectedEmailId }) {
  // Always define hooks at the top level, before any conditional returns
  const formatDate = useMemo(() => {
    return (dateString) => {
      if (!dateString) return '';
      
      const date = new Date(dateString);
      const now = new Date();
      const yesterday = new Date(now);
      yesterday.setDate(yesterday.getDate() - 1);
      
      // Today
      if (date.toDateString() === now.toDateString()) {
        return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
      }
      // Yesterday
      else if (date.toDateString() === yesterday.toDateString()) {
        return 'Yesterday';
      }
      // This week
      else if (now - date < 7 * 24 * 60 * 60 * 1000) {
        return date.toLocaleDateString([], { weekday: 'short' });
      }
      // This year
      else if (date.getFullYear() === now.getFullYear()) {
        return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
      }
      // Earlier years
      else {
        return date.toLocaleDateString([], { year: 'numeric', month: 'short', day: 'numeric' });
      }
    };
  }, []);

  // Group emails by date sections for better organization
  const groupedEmails = useMemo(() => {
    const now = new Date();
    const today = now.toDateString();
    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);
    const yesterdayStr = yesterday.toDateString();
    
    const groups = {
      today: [],
      yesterday: [],
      thisWeek: [],
      thisMonth: [],
      older: []
    };
    
    // Only process if there are emails
    if (emails && emails.length > 0) {
      emails.forEach(email => {
        const emailDate = new Date(email.date);
        const emailDateStr = emailDate.toDateString();
        
        if (emailDateStr === today) {
          groups.today.push(email);
        } else if (emailDateStr === yesterdayStr) {
          groups.yesterday.push(email);
        } else if (now - emailDate < 7 * 24 * 60 * 60 * 1000) {
          groups.thisWeek.push(email);
        } else if (now - emailDate < 30 * 24 * 60 * 60 * 1000) {
          groups.thisMonth.push(email);
        } else {
          groups.older.push(email);
        }
      });
    }
    
    return groups;
  }, [emails]);

  // Now you can have your conditional return
  if (!emails || emails.length === 0) {
    return (
      <div className="text-center p-5">
        <i className="bi bi-inbox text-muted" style={{ fontSize: '2rem' }}></i>
        <h5 className="mt-3">No emails found</h5>
        <p className="text-muted">Your inbox is empty</p>
      </div>
    );
  }

  return (
    <div className="email-list-container">
      {/* Search and filter options */}
      <div className="email-list-header p-2 border-bottom">
        <div className="input-group input-group-sm">
          <span className="input-group-text bg-light border-end-0">
            <i className="bi bi-search"></i>
          </span>
          <input 
            type="text" 
            className="form-control form-control-sm border-start-0 bg-light" 
            placeholder="Search emails"
            aria-label="Search emails"
          />
        </div>
      </div>
      
      <ListGroup className="email-list">
        {/* Today's emails */}
        {groupedEmails.today.length > 0 && (
          <>
            <div className="email-date-header px-3 py-1 bg-light">
              <small className="text-muted">Today</small>
            </div>
            {groupedEmails.today.map((email) => (
              <EmailItem 
                key={email.id} 
                email={email} 
                isSelected={email.id === selectedEmailId}
                onSelect={onSelectEmail}
                formatDate={formatDate}
              />
            ))}
          </>
        )}
        
        {/* Yesterday's emails */}
        {groupedEmails.yesterday.length > 0 && (
          <>
            <div className="email-date-header px-3 py-1 bg-light">
              <small className="text-muted">Yesterday</small>
            </div>
            {groupedEmails.yesterday.map((email) => (
              <EmailItem 
                key={email.id} 
                email={email} 
                isSelected={email.id === selectedEmailId}
                onSelect={onSelectEmail}
                formatDate={formatDate}
              />
            ))}
          </>
        )}
        
        {/* This week's emails */}
        {groupedEmails.thisWeek.length > 0 && (
          <>
            <div className="email-date-header px-3 py-1 bg-light">
              <small className="text-muted">This Week</small>
            </div>
            {groupedEmails.thisWeek.map((email) => (
              <EmailItem 
                key={email.id} 
                email={email} 
                isSelected={email.id === selectedEmailId}
                onSelect={onSelectEmail}
                formatDate={formatDate}
              />
            ))}
          </>
        )}
        
        {/* This month's emails */}
        {groupedEmails.thisMonth.length > 0 && (
          <>
            <div className="email-date-header px-3 py-1 bg-light">
              <small className="text-muted">This Month</small>
            </div>
            {groupedEmails.thisMonth.map((email) => (
              <EmailItem 
                key={email.id} 
                email={email} 
                isSelected={email.id === selectedEmailId}
                onSelect={onSelectEmail}
                formatDate={formatDate}
              />
            ))}
          </>
        )}
        
        {/* Older emails */}
        {groupedEmails.older.length > 0 && (
          <>
            <div className="email-date-header px-3 py-1 bg-light">
              <small className="text-muted">Older</small>
            </div>
            {groupedEmails.older.map((email) => (
              <EmailItem 
                key={email.id} 
                email={email} 
                isSelected={email.id === selectedEmailId}
                onSelect={onSelectEmail}
                formatDate={formatDate}
              />
            ))}
          </>
        )}
      </ListGroup>
    </div>
  );
}

export default EmailList;
